import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { debounce } from "../../util/debounce";
import { humanizeSlug } from "../../util/format";
import { getBreakpoint } from "../../util/itemsPerPage";

import ItemList from "../../components/ItemList";
import Breadcrumb from "../../components/Breadcrumb";

import {
  ButtonFilterMobile,
  Container,
  ContainerFilter,
  ContainerHeaderItemList,
  ContainerProductFound,
  FilterInMobile,
  ProductContainer
} from "./styles";

import { useSelector } from "react-redux";
import Dropdown from "../../components/DropdownFilter";
import { DropdownSelectOrdenation } from "../../components/DropdownSelectOrdenation";
import HelmetCustom from "../../components/HelmetCustom";
import { FilterContext } from "../../contexts/FilterContext";
import { OrdenationContext } from "../../contexts/OrdenationContext";

import { FiFilter } from "react-icons/fi";
import { FilterContainer } from "../../components/ItemList/styles";
import MainLayout from "../../components/MainLayout";

export default function SearchResultProducts({ history, match }) {
  // Decodifica o termo da URL para lidar com caracteres especiais
  const termReceived = match.params.term ? decodeURIComponent(match.params.term) : ""
  const location = useLocation();
  const { productsFound, loading, totalizadorGeral } = useSelector(
    state => state.search
  );
  const [hasModalOpen, setHasModalOpen] = useState(false);
  const [isViewMobile, setIsViewMobile] = useState(window.innerWidth <= 520);
  const { setOrdenations, ordenation } = useContext(OrdenationContext);
  const { breadcrumb } = useContext(FilterContext);
  const [title, setTitle] = useState("");

  const options = [
    { value: "relevancia", label: "Relevância" },
    { value: "maior-preco", label: "Maior Preço" },
    { value: "menor-preco", label: "Menor Preço" },
    { value: "lancamento", label: "Lançamento" },
  ];

  // Sincroniza ordenação da URL com o contexto no carregamento inicial
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlOrdering = params.get('ordering');

    // Se tem ordering na URL e é diferente do contexto, atualiza o contexto
    if (urlOrdering && urlOrdering !== ordenation) {
      setOrdenations(urlOrdering);
    } else if (!urlOrdering && !ordenation) {
      // Se não tem na URL e não tem no contexto, define o padrão
      setOrdenations('relevancia');
    }
  }, [location.pathname, location.search, ordenation, setOrdenations]); // Executa quando a rota muda

  // Nome amigável da entidade atual (categoria/sub/segmento), com fallback de-slug.
  const entityName = useMemo(() => {
    if (match.params.ctgId) {
      return breadcrumb.category || humanizeSlug(match.params.ctg);
    }
    if (match.params.subCtgId) {
      return breadcrumb.subcategory || humanizeSlug(match.params.subCtg);
    }
    if (match.params.segId) {
      return breadcrumb.segment || humanizeSlug(match.params.seg);
    }
    return "";
  }, [
    breadcrumb,
    match.params.ctgId,
    match.params.ctg,
    match.params.subCtgId,
    match.params.subCtg,
    match.params.segId,
    match.params.seg,
  ]);

  // Título dinâmico conforme a rota/escolha do usuário.
  useEffect(() => {
    if (termReceived) {
      setTitle(`Resultado da busca para: "${termReceived}"`);
    } else {
      setTitle(entityName || "Produtos");
    }
  }, [termReceived, entityName]);

  // Itens do breadcrumb (Início é adicionado pelo componente).
  const breadcrumbItems = useMemo(() => {
    const items = [{ name: "Produtos", to: "/produtos" }];
    if (termReceived) {
      items.push({ name: `Resultado da busca para: "${termReceived}"` });
    } else if (entityName) {
      items.push({ name: entityName });
    }
    return items;
  }, [termReceived, entityName]);

  // Troca de ordenação: fonte única de verdade é a URL (que dispara a busca no
  // DropdownFilter). Empurra o novo ordering direto para a URL e reseta a página.
  const handleOrderingChange = (value) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('page', '1');
    params.set('ordering', value);
    url.search = params.toString();
    history.push(url.pathname + url.search);
  };

  // Limpa todos os filtros (mesma semântica do "Limpar" do painel): volta para a
  // busca base, reseta page=1 e preserva a ordenação. O DropdownFilter re-sincroniza
  // os filtros a partir da nova URL.
  const handleClearFilters = () => {
    const params = new URLSearchParams();
    params.set('page', '1');
    if (ordenation) params.set('ordering', ordenation);
    history.push(`/produtos/buscar?${params.toString()}`);
  };

  useEffect(() => {
    const setStyle = (selector, prop, value) => {
      const el = selector.startsWith("#")
        ? document.getElementById(selector.slice(1))
        : document.querySelector(selector);
      if (el) el.style[prop] = value;
    };

    if (hasModalOpen) {
      setStyle("#filterMobile", "visibility", "visible");
      setStyle("#filterMobile", "opacity", "1");
      setStyle("#filterMobile", "pointerEvents", "auto");
      setStyle("header", "display", "none");
      setStyle("#searchStore", "display", "none");
      setStyle("#containerMainPage", "display", "none");
    } else {
      setStyle("header", "display", "block");
      setStyle("#searchStore", "display", "flex");
      setStyle("#filterMobile", "visibility", "hidden");
      setStyle("#filterMobile", "opacity", "0");
      setStyle("#filterMobile", "pointerEvents", "none");
      setStyle("#containerMainPage", "display", "block");
    }
  }, [hasModalOpen])

  useEffect(() => {
    function createElementFilterResponsive() {
      setIsViewMobile(window.innerWidth <= 520);
    }
    window.addEventListener('pageshow', createElementFilterResponsive);
    window.addEventListener('resize', createElementFilterResponsive);
    return () => {
      window.removeEventListener('pageshow', createElementFilterResponsive);
      window.removeEventListener('resize', createElementFilterResponsive);
    };
  }, []);

  // Ao cruzar um breakpoint, o page size (itens por página) muda. Reseta para a
  // página 1 para evitar offset inconsistente e forçar nova busca com o novo limit.
  const breakpointRef = useRef(getBreakpoint());
  useEffect(() => {
    const handleResize = debounce(() => {
      const current = getBreakpoint();
      if (current !== breakpointRef.current) {
        breakpointRef.current = current;
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        params.set('page', '1');
        url.search = params.toString();
        history.push(url.pathname + url.search);
      }
    }, 300);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [history]);

  const showProductsCount = (count) => {
    return (
      <ContainerProductFound>
        {count > 0 && (
          <span>{count} produtos encontrados</span>
        )}
      </ContainerProductFound>
    );
  }

  return (
    <MainLayout history={history}>
      <HelmetCustom
        pageTitle={title}
        content={title}
        fbDescription={title}
        noindex={!match.params.ctg && !match.params.subCtg && !!match.params.term && !match.params.seg} // Adiciona noindex para páginas de busca por termo sem categoria ou segmento
      />
      <FilterInMobile id="filterMobile">
        <Dropdown params={match.params && match.params} isModal={true} setIsOpen={() => {
          setHasModalOpen(!hasModalOpen)
        }} />
      </FilterInMobile>
      <ContainerHeaderItemList>
        <Breadcrumb items={breadcrumbItems} />
        <div className="header-main">
          <div className="titles">
            {title && <h1>{title}</h1>}
            {showProductsCount(totalizadorGeral)}
          </div>
          <ContainerFilter hasMarginTop={isViewMobile}>
            {isViewMobile && (
              <ButtonFilterMobile onClick={() => {
                setHasModalOpen(!hasModalOpen)
              }}>
                <span>Filtro</span>
                <FiFilter size={18} />
              </ButtonFilterMobile>
            )}
            <div className="ordenation">
              <DropdownSelectOrdenation options={options} onChange={handleOrderingChange} value={ordenation} />
            </div>
          </ContainerFilter>
        </div>
      </ContainerHeaderItemList>
      <div id="containerMainPage">
        <Container>

          <FilterContainer>
            {!isViewMobile && (
              <Dropdown params={match.params} isModal={false} />
            )}
          </FilterContainer>

          <ProductContainer>


            <ItemList
              products={productsFound}
              loading={loading}
              haveFilter={true}
              searchTerm={match.params}
              isMobile={isViewMobile}
              totalizadorGeral={totalizadorGeral}
              onClearFilters={handleClearFilters}
            />


          </ProductContainer>

        </Container>
      </div>
    </MainLayout>
  );
}
